/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Jan 22 2024
 **/

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useFileManager } from "@/frameworks/core/hooks";
import { Column, TypographyTooltip } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";

import * as S from "./emotion";

type Folder = FileBox.Folders.GetMyFoldersResponse[number] & {
	isMyFile: boolean;
	parent?: Folder;
	referenceType: "INDIVIDUAL";
	referenceID: string;
	folderType: "INDIVIDUAL";
};

export default function SubFolder({
	folder,
	...rest
}: {
	folder: Folder;
	focusedBy: string;
	onSelectFolder: (targetFolder: Folder, targetParentFolder?: Folder) => void;
}) {
	const { getMyFolders, myRoomID } = useFileManager();
	const [enabled, setEnabled] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const queryClient = useQueryClient();
	const queriedFolders = queryClient.getQueryData<FileBox.Folders.GetMyFoldersResponse>(["myFolders", folder.id]);

	const { data } = useQuery({
		queryKey: ["myFolders", folder.id],
		queryFn: async () => {
			return await getMyFolders({ id: folder.id });
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchInterval: 1000 * 60 * 5,
		enabled: !queriedFolders && enabled && folder.existsSubFolder
	});

	const handleSelect = (targetFolder: Folder) => {
		setIsOpen(!isOpen);
		setEnabled(true);
		rest.onSelectFolder(targetFolder);
	};

	const subFolders = queriedFolders ?? data;

	return (
		<Column.li css={{ width: "100%", paddingLeft: "20px" }} gap={4}>
			<S.FileBoxWrapper
				css={{ width: "100%", padding: folder.existsSubFolder ? "4px 6px" : undefined }}
				gap={4}
				verticalAlign="center"
				focus={folder.id === rest.focusedBy}
				onClick={() => {
					handleSelect(folder);
				}}
			>
				<Maybe test={folder.existsSubFolder}>
					<S.MyFileArrowIcon open={false} name="DownFilledBig16" size={16} />
				</Maybe>
				<TypographyTooltip.p
					tooltipProps={{
						hasIcon: true
					}}
					componentProps={{
						typoSize: "Title3",
						typoColor: "neutral_80_100",
						ellipsis: true,
						maxLine: 1,
						stretch: true,
						align: "left"
					}}
				>
					{folder.name}
				</TypographyTooltip.p>
			</S.FileBoxWrapper>
			<Maybe test={Boolean(subFolders)}>
				<Column.ul
					gap={4}
					css={{
						width: "100%",
						height: !isOpen ? "0px" : "initial",
						overflow: "hidden"
					}}
				>
					{subFolders?.map((subFolder) => {
						return (
							<SubFolder
								key={subFolder.id}
								folder={{
									...subFolder,
									parent: folder,
									isMyFile: true,
									referenceType: "INDIVIDUAL",
									referenceID: myRoomID,
									folderType: "INDIVIDUAL"
								}}
								onSelectFolder={rest.onSelectFolder}
								focusedBy={rest.focusedBy}
							/>
						);
					})}
				</Column.ul>
			</Maybe>
		</Column.li>
	);
}
